"use client";

import React, { useState } from "react";
import { Play, FileText, Loader, Info } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
function VideoLibraryConsumer() {
  const {
    posts,
    loading,
    error,
    setQuery,
    isRefetching,
    hasNext,
    loadMore
  } = usePosts();
  const [activeTab, setActiveTab] = useState("preschool");
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setQuery({
      search: value,
      page: 1
    });
  };
  return <div className="w-full space-y-8">
      
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full flex flex-col items-center">
        <TabsList className="grid w-full max-w-md grid-cols-3 bg-secondary/50 p-1">
          <TabsTrigger value="preschool" className="data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-md transition-all cursor-pointer">
            Preschool
          </TabsTrigger>
          <TabsTrigger value="school age" className="data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-md transition-all cursor-pointer">
            School Age
          </TabsTrigger>
          <TabsTrigger value="teen" className="data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-md transition-all cursor-pointer">
            Teen
          </TabsTrigger>
        </TabsList>
      </Tabs>

      
      {loading && !isRefetching && posts.length === 0 ? <div className="flex justify-center py-20">
          <Loader className="h-10 w-10 animate-spin text-primary/50" />
        </div> : error ? <div className="text-center py-12 text-muted-foreground">
          <p>Unable to load videos at this time.</p>
          <Button variant="link" onClick={() => setQuery({
        page: 1
      })}>Try Again</Button>
        </div> : <div className="space-y-10">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {posts.length > 0 ? posts.map((post, i) => <React.Fragment key={post.id}>
                  <Card data-index={i} className="group overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
                    
                    <div className="relative aspect-video w-full overflow-hidden bg-muted">
                      <Link to={post.link || "#"} className="block w-full h-full focus:outline-none">
                        {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-secondary/30 text-muted-foreground">
                            <Play className="h-12 w-12 opacity-50" />
                          </div>}
                        
                        
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                          <div className="rounded-full bg-background/90 p-3 text-primary shadow-lg backdrop-blur-sm">
                            <Play className="h-6 w-6 fill-current" />
                          </div>
                        </div>

                        
                        <div className="absolute inset-0 pointer-events-none ring-0 transition-all duration-300 group-hover:ring-4 group-hover:ring-[var(--color-ring)]/50 group-focus-within:ring-4 group-focus-within:ring-[var(--color-ring)]/50 inset-ring-offset-2" />
                      </Link>
                    </div>

                    <CardHeader className="p-5 pb-2">
                      <div className="flex justify-between items-start gap-2">
                        <CardTitle className="text-lg font-serif leading-tight text-primary line-clamp-2">
                          <Link to={post.link || "#"} className="hover:text-primary/80 transition-colors">
                            {post.title}
                          </Link>
                        </CardTitle>
                      </div>
                    </CardHeader>

                    <CardContent className="p-5 pt-2 pb-4">
                      <CardDescription className="line-clamp-2 text-sm">
                        {post.excerpt ? <span dangerouslySetInnerHTML={{
                  __html: post.excerpt
                }} /> : "A therapeutic session designed to help children process emotions through storytelling."}
                      </CardDescription>
                    </CardContent>

                    <CardFooter className="p-5 pt-0 flex items-center justify-between border-t border-border/40 mt-auto">
                      <Link to={post.link || "#"}>
                        <Button variant="ghost" size="sm" className="text-xs font-medium text-primary hover:bg-secondary/50 -ml-2">
                          Watch Video
                        </Button>
                      </Link>

                      
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs border-primary/20 hover:border-primary/40 hover:bg-primary/5 cursor-help">
                            <Info className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Clinician Notes</span>
                          </Button>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80 p-4 shadow-xl border-primary/10 bg-background/95 backdrop-blur-md">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-primary">
                              <FileText className="h-4 w-4" />
                              <h4 className="font-semibold text-sm">Therapeutic Context</h4>
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {post.customFields?.clinician_notes || "This video focuses on emotional regulation and narrative building. Recommended for children experiencing transition or mild anxiety. Use as a guided activity with a caregiver present."}
                            </p>
                            <div className="flex gap-2 pt-2">
                              {post.tags?.slice(0, 3).map((tag, idx) => <span key={idx} className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                  {tag.name}
                                </span>)}
                            </div>
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    </CardFooter>
                  </Card>

                  {i === 0 && <>
                      <Card className="group overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
                        <div className="relative aspect-video w-full overflow-hidden bg-muted">
                          <Link to="https://youtu.be/CMJRY9YsCxg" newTab className="block w-full h-full focus:outline-none">
                            <img src="https://img.youtube.com/vi/CMJRY9YsCxg/hqdefault.jpg" alt="The Brave Little Drop - Blood Draw" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                              <div className="rounded-full bg-background/90 p-3 text-primary shadow-lg backdrop-blur-sm">
                                <Play className="h-6 w-6 fill-current" />
                              </div>
                            </div>
                            <div className="absolute inset-0 pointer-events-none ring-0 transition-all duration-300 group-hover:ring-4 group-hover:ring-[var(--color-ring)]/50 group-focus-within:ring-4 group-focus-within:ring-[var(--color-ring)]/50 inset-ring-offset-2" />
                          </Link>
                        </div>

                        <CardHeader className="p-5 pb-2">
                          <div className="flex justify-between items-start gap-2">
                            <CardTitle className="text-lg font-serif leading-tight text-primary line-clamp-2">
                              <Link to="https://youtu.be/CMJRY9YsCxg" newTab className="hover:text-primary/80 transition-colors">
                                The Brave Little Drop - Blood Draw
                              </Link>
                            </CardTitle>
                          </div>
                        </CardHeader>

                        <CardContent className="p-5 pt-2 pb-4">
                          <CardDescription className="line-clamp-2 text-sm">
                            Follow the brave little drop on a journey to help doctors learn more about your health. A friendly guide to understanding blood draws for children and families.
                          </CardDescription>
                        </CardContent>

                        <CardFooter className="p-5 pt-0 flex items-center justify-between border-t border-border/40 mt-auto">
                          <Link to="https://youtu.be/CMJRY9YsCxg" newTab>
                            <Button variant="ghost" size="sm" className="text-xs font-medium text-primary hover:bg-secondary/50 -ml-2">
                              Watch Video
                            </Button>
                          </Link>

                          <HoverCard>
                            <HoverCardTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs border-primary/20 hover:border-primary/40 hover:bg-primary/5 cursor-help">
                                <Info className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Clinician Notes</span>
                              </Button>
                            </HoverCardTrigger>
                            <HoverCardContent className="w-80 p-4 shadow-xl border-primary/10 bg-background/95 backdrop-blur-md">
                              <div className="space-y-3">
                                <div className="flex items-center gap-2 text-primary">
                                  <FileText className="h-4 w-4" />
                                  <h4 className="font-semibold text-sm">Therapeutic Context</h4>
                                </div>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                  This video focuses on procedural education and anxiety reduction for blood draws. It uses gentle storytelling to help children understand the process and build emotional resilience before medical visits.
                                </p>
                                <div className="flex gap-2 pt-2">
                                  <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                    Procedural
                                  </span>
                                  <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                    Anxiety
                                  </span>
                                  <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                    Education
                                  </span>
                                </div>
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                        </CardFooter>
                      </Card>

                      <Card className="group overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
                        <div className="relative aspect-video w-full overflow-hidden bg-muted">
                          <Link to="https://youtu.be/sHqRuFnggCs" newTab className="block w-full h-full focus:outline-none">
                            <img src="https://img.youtube.com/vi/sHqRuFnggCs/hqdefault.jpg" alt="A Small Journey Through The Tunnel - MRI" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                              <div className="rounded-full bg-background/90 p-3 text-primary shadow-lg backdrop-blur-sm">
                                <Play className="h-6 w-6 fill-current" />
                              </div>
                            </div>
                            <div className="absolute inset-0 pointer-events-none ring-0 transition-all duration-300 group-hover:ring-4 group-hover:ring-[var(--color-ring)]/50 group-focus-within:ring-4 group-focus-within:ring-[var(--color-ring)]/50 inset-ring-offset-2" />
                          </Link>
                        </div>

                        <CardHeader className="p-5 pb-2">
                          <div className="flex justify-between items-start gap-2">
                            <CardTitle className="text-lg font-serif leading-tight text-primary line-clamp-2">
                              <Link to="https://youtu.be/sHqRuFnggCs" newTab className="hover:text-primary/80 transition-colors">
                                A Small Journey Through The Tunnel - MRI
                              </Link>
                            </CardTitle>
                          </div>
                        </CardHeader>

                        <CardContent className="p-5 pt-2 pb-4">
                          <CardDescription className="line-clamp-2 text-sm">
                            Step into the world of MRI with this friendly guide. Learn about the sounds, the space, and how staying still helps doctors take clear pictures.
                          </CardDescription>
                        </CardContent>

                        <CardFooter className="p-5 pt-0 flex items-center justify-between border-t border-border/40 mt-auto">
                          <Link to="https://youtu.be/sHqRuFnggCs" newTab>
                            <Button variant="ghost" size="sm" className="text-xs font-medium text-primary hover:bg-secondary/50 -ml-2">
                              Watch Video
                            </Button>
                          </Link>

                          <HoverCard>
                            <HoverCardTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs border-primary/20 hover:border-primary/40 hover:bg-primary/5 cursor-help">
                                <Info className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Clinician Notes</span>
                              </Button>
                            </HoverCardTrigger>
                            <HoverCardContent className="w-80 p-4 shadow-xl border-primary/10 bg-background/95 backdrop-blur-md">
                              <div className="space-y-3">
                                <div className="flex items-center gap-2 text-primary">
                                  <FileText className="h-4 w-4" />
                                  <h4 className="font-semibold text-sm">Therapeutic Context</h4>
                                </div>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                  This video provides sensory preparation for MRI procedures. It helps children understand the environment and sounds of the machine, reducing anxiety through familiarity and clear expectations.
                                </p>
                                <div className="flex gap-2 pt-2">
                                  <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                    Procedural
                                  </span>
                                  <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                    Sensory
                                  </span>
                                  <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                    MRI
                                  </span>
                                </div>
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                        </CardFooter>
                      </Card>

                      <Card className="group overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
                        <div className="relative aspect-video w-full overflow-hidden bg-muted">
                          <Link to="https://youtu.be/HoS-JtEpKjA" newTab className="block w-full h-full focus:outline-none">
                            <img src="https://img.youtube.com/vi/HoS-JtEpKjA/hqdefault.jpg" alt="Preparing for Anesthesia with Luna" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                              <div className="rounded-full bg-background/90 p-3 text-primary shadow-lg backdrop-blur-sm">
                                <Play className="h-6 w-6 fill-current" />
                              </div>
                            </div>
                            <div className="absolute inset-0 pointer-events-none ring-0 transition-all duration-300 group-hover:ring-4 group-hover:ring-[var(--color-ring)]/50 group-focus-within:ring-4 group-focus-within:ring-[var(--color-ring)]/50 inset-ring-offset-2" />
                          </Link>
                        </div>

                        <CardHeader className="p-5 pb-2">
                          <div className="flex justify-between items-start gap-2">
                            <CardTitle className="text-lg font-serif leading-tight text-primary line-clamp-2">
                              <Link to="https://youtu.be/HoS-JtEpKjA" newTab className="hover:text-primary/80 transition-colors">
                                Preparing for Anesthesia with Luna
                              </Link>
                            </CardTitle>
                          </div>
                        </CardHeader>

                        <CardContent className="p-5 pt-2 pb-4">
                          <CardDescription className="line-clamp-2 text-sm">
                            Join Luna as she discovers the gentle process of falling asleep for a medical procedure. A comforting guide to understanding anesthesia for children.
                          </CardDescription>
                        </CardContent>

                        <CardFooter className="p-5 pt-0 flex items-center justify-between border-t border-border/40 mt-auto">
                          <Link to="https://youtu.be/HoS-JtEpKjA" newTab>
                            <Button variant="ghost" size="sm" className="text-xs font-medium text-primary hover:bg-secondary/50 -ml-2">
                              Watch Video
                            </Button>
                          </Link>

                          <HoverCard>
                            <HoverCardTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs border-primary/20 hover:border-primary/40 hover:bg-primary/5 cursor-help">
                                <Info className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Clinician Notes</span>
                              </Button>
                            </HoverCardTrigger>
                            <HoverCardContent className="w-80 p-4 shadow-xl border-primary/10 bg-background/95 backdrop-blur-md">
                              <div className="space-y-3">
                                <div className="flex items-center gap-2 text-primary">
                                  <FileText className="h-4 w-4" />
                                  <h4 className="font-semibold text-sm">Therapeutic Context</h4>
                                </div>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                  This video uses the 'resting cloud' metaphor to explain anesthesia. It focuses on sensory preparation and building trust with the medical team. Recommended for pre-operative preparation.
                                </p>
                                <div className="flex gap-2 pt-2">
                                  <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                    Anesthesia
                                  </span>
                                  <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                    Procedural
                                  </span>
                                  <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                    Resilience
                                  </span>
                                </div>
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                        </CardFooter>
                      </Card>

                      <Card className="group overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
                        <div className="relative aspect-video w-full overflow-hidden bg-muted">
                          <Link to="https://youtu.be/7rtyAWRvTps" newTab className="block w-full h-full focus:outline-none">
                            <img src="https://img.youtube.com/vi/7rtyAWRvTps/hqdefault.jpg" alt="The Resting Cloud - A Calming Journey" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                              <div className="rounded-full bg-background/90 p-3 text-primary shadow-lg backdrop-blur-sm">
                                <Play className="h-6 w-6 fill-current" />
                              </div>
                            </div>
                            <div className="absolute inset-0 pointer-events-none ring-0 transition-all duration-300 group-hover:ring-4 group-hover:ring-[var(--color-ring)]/50 group-focus-within:ring-4 group-focus-within:ring-[var(--color-ring)]/50 inset-ring-offset-2" />
                          </Link>
                        </div>

                        <CardHeader className="p-5 pb-2">
                          <div className="flex justify-between items-start gap-2">
                            <CardTitle className="text-lg font-serif leading-tight text-primary line-clamp-2">
                              <Link to="https://youtu.be/7rtyAWRvTps" newTab className="hover:text-primary/80 transition-colors">
                                The Resting Cloud - A Calming Journey
                              </Link>
                            </CardTitle>
                          </div>
                        </CardHeader>

                        <CardContent className="p-5 pt-2 pb-4">
                          <CardDescription className="line-clamp-2 text-sm">
                            A gentle storytelling session focused on relaxation and emotional regulation. Perfect for finding peace before or after a medical visit.
                          </CardDescription>
                        </CardContent>

                        <CardFooter className="p-5 pt-0 flex items-center justify-between border-t border-border/40 mt-auto">
                          <Link to="https://youtu.be/7rtyAWRvTps" newTab>
                            <Button variant="ghost" size="sm" className="text-xs font-medium text-primary hover:bg-secondary/50 -ml-2">
                              Watch Video
                            </Button>
                          </Link>

                          <HoverCard>
                            <HoverCardTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs border-primary/20 hover:border-primary/40 hover:bg-primary/5 cursor-help">
                                <Info className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Clinician Notes</span>
                              </Button>
                            </HoverCardTrigger>
                            <HoverCardContent className="w-80 p-4 shadow-xl border-primary/10 bg-background/95 backdrop-blur-md">
                              <div className="space-y-3">
                                <div className="flex items-center gap-2 text-primary">
                                  <FileText className="h-4 w-4" />
                                  <h4 className="font-semibold text-sm">Therapeutic Context</h4>
                                </div>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                  Focuses on somatic regulation and guided imagery. The 'resting cloud' serves as a portable coping tool children can use in high-stress environments to maintain a sense of calm and safety.
                                </p>
                                <div className="flex gap-2 pt-2">
                                  <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                    Relaxation
                                  </span>
                                  <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                    Coping Tools
                                  </span>
                                  <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                                    Imagery
                                  </span>
                                </div>
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                        </CardFooter>
                      </Card>
                    </>}
                </React.Fragment>) : <div className="col-span-full text-center py-12 bg-muted/30 rounded-2xl border border-dashed border-border">
                <p className="text-muted-foreground">No videos found for this age group.</p>
              </div>}
          </div>

          
          {hasNext && <div className="flex justify-center pt-8">
              <Button onClick={() => loadMore()} disabled={isRefetching} variant="outline" size="lg" className="min-w-[150px] border-primary/20 hover:bg-primary/5">
                {isRefetching ? <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </> : "Load More Videos"}
              </Button>
            </div>}
        </div>}
    </div>;
}
export default function TherapeuticVideoCollection() {
  const wp_query_params = {
    per_page: 6,
    search: "preschool",
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="therapeutic-video-collection" className="py-20 bg-background relative overflow-hidden">
      
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-[var(--color-secondary)]/20 blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] rounded-full bg-[var(--color-accent)]/10 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-12 md:mb-16 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-primary mb-6 tracking-tight">
            Therapeutic Video Collection
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            A safe, curated library of storytelling videos designed to help children and caregivers navigate complex emotions. 
            Filter by age group to find the most appropriate resources for your needs.
          </p>
        </div>

        <PostsProvider wp_query={{
        per_page: 6,
        search: "preschool",
        orderby: "date",
        order: "desc",
        status: ["publish"]
      }} className="min-h-[400px]">
          <VideoLibraryConsumer />
        </PostsProvider>
      </div>
    </section>;
}